import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import '@awesome.me/webawesome/dist/components/button/button.js';
import '@awesome.me/webawesome/dist/components/callout/callout.js';
import '@awesome.me/webawesome/dist/components/dialog/dialog.js';
import '@awesome.me/webawesome/dist/components/input/input.js';
import '@awesome.me/webawesome/dist/components/option/option.js';
import '@awesome.me/webawesome/dist/components/select/select.js';
import '@awesome.me/webawesome/dist/components/tag/tag.js';

import { ApiError, api, formValues, leaveKindLabels, type LeaveSummary, type VacationAnchor } from '../api.js';
import { pageStyles } from '../shared-styles.js';

type Period = 'last365' | 'this_year' | 'last_year';

const periodLabels: Record<Period, string> = { last365: 'Last 365 days', this_year: 'This year', last_year: 'Last year' };

const isoDate = (d: Date) => d.toISOString().slice(0, 10);

/** from/to of a period, computed in UTC so the dates do not shift with the browser zone. */
export function periodRange(period: Period, today = new Date()): { from: string; to: string } {
  const y = today.getUTCFullYear();
  switch (period) {
    case 'this_year':
      return { from: `${y}-01-01`, to: `${y}-12-31` };
    case 'last_year':
      return { from: `${y - 1}-01-01`, to: `${y - 1}-12-31` };
    default: {
      const from = new Date(Date.UTC(y, today.getUTCMonth(), today.getUTCDate() - 365));
      return { from: isoDate(from), to: isoDate(today) };
    }
  }
}

/**
 * Vacation and sick leave of one employee over a chosen period, the balance
 * anchors and the balance from the latest anchor. Entries are read-only here;
 * anchors can be added and removed. dialog.show(employeeId, name).
 */
@customElement('vacation-dialog')
export class VacationDialog extends LitElement {
  static styles = pageStyles;

  @state() private isOpen = false;
  @state() private employeeId = 0;
  @state() private name = '';
  @state() private period: Period = 'last365';
  @state() private summary: LeaveSummary | null = null;
  @state() private error = '';
  @state() private saving = false;

  async show(employeeId: number, name: string) {
    this.employeeId = employeeId;
    this.name = name;
    this.summary = null;
    this.error = '';
    this.isOpen = true;
    await this.load();
  }

  private async load() {
    const { from, to } = periodRange(this.period);
    try {
      this.summary = await api.get<LeaveSummary>(`/api/leave/summary?employee_id=${this.employeeId}&from=${from}&to=${to}`);
    } catch (e) {
      this.error = (e as Error).message;
    }
  }

  private onHidden = (e: Event) => {
    if (e.target === e.currentTarget) {
      this.isOpen = false;
      this.dispatchEvent(new CustomEvent('closed', { bubbles: true, composed: true }));
    }
  };

  private async setPeriod(e: Event) {
    this.period = (e.target as HTMLSelectElement).value as Period;
    await this.load();
  }

  private async addAnchor(e: Event) {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const v = formValues(form);
    await this.call(async () => {
      await api.post('/api/vacation-anchors', { employee_id: this.employeeId, as_of: v.as_of, remaining_days: v.remaining_days, note: v.note });
      form.reset();
    });
  }

  private async removeAnchor(a: VacationAnchor) {
    if (!confirm(`Remove the anchor of ${a.as_of} (${a.remaining_days} days)?`)) return;
    await this.call(() => api.delete(`/api/vacation-anchors/${a.id}`));
  }

  private async call(fn: () => Promise<unknown>) {
    this.saving = true;
    this.error = '';
    try {
      await fn();
      await this.load();
    } catch (err) {
      if (err instanceof ApiError) {
        const hint = Object.values(err.fields)[0];
        this.error = hint ? `${err.message}: ${hint}` : err.message;
      } else {
        this.error = (err as Error).message;
      }
    } finally {
      this.saving = false;
    }
  }

  render() {
    const s = this.summary;
    return html`
      <wa-dialog label=${`Vacation · ${this.name}`} ?open=${this.isOpen} @wa-after-hide=${this.onHidden} style="--width: min(52rem, calc(100vw - 1.5rem));">
        ${this.error ? html`<wa-callout variant="danger">${this.error}</wa-callout>` : nothing}
        ${s ? this.renderBalance(s) : nothing}

        <div class="toolbar">
          <wa-select label="Period" size="small" value=${this.period} @change=${this.setPeriod}>
            ${(Object.keys(periodLabels) as Period[]).map((p) => html`<wa-option value=${p}>${periodLabels[p]}</wa-option>`)}
          </wa-select>
          ${s ? html`<span class="muted">${s.from} → ${s.to}</span>` : nothing}
        </div>
        ${s ? this.renderEntries(s) : html`<p class="muted">Loading…</p>`}
        ${s ? this.renderAnchors(s) : nothing}
      </wa-dialog>
    `;
  }

  private renderBalance(s: LeaveSummary) {
    const b = s.balance;
    if (!b) {
      return html`<wa-callout variant="neutral">No balance anchor yet. Add the remaining days as of a date below; vacation taken from that date on is subtracted.</wa-callout>`;
    }
    const short = Number(b.balance) < 0;
    return html`
      <wa-callout variant=${short ? 'warning' : 'success'}>
        <strong>${b.balance} days</strong> of vacation remaining · anchor ${b.remaining} as of ${b.as_of} · ${b.taken_since} taken since (booked days ahead included)
      </wa-callout>
    `;
  }

  private renderEntries(s: LeaveSummary) {
    return html`
      <h4>Leave in this period</h4>
      <p class="muted">
        Vacation <strong>${s.totals.vacation}</strong> · Sick <strong>${s.totals.sick}</strong> · Other <strong>${s.totals.other}</strong> days
      </p>
      ${s.entries.length === 0
        ? html`<p class="muted">No leave recorded in this period.</p>`
        : html`
            <div class="table-wrap"><table class="entries">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Kind</th>
                  <th class="num">Days</th>
                  <th>Source</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                ${s.entries.map(
                  (d) => html`
                    <tr>
                      <td>${d.date}</td>
                      <td>${d.kind === 'sick' ? html`<wa-tag size="small" variant="warning">${leaveKindLabels[d.kind]}</wa-tag>` : leaveKindLabels[d.kind]}</td>
                      <td class="num">${d.fraction}</td>
                      <td>${d.source === 'everhour' ? html`Everhour${d.client_name ? ` (${d.client_name})` : ''}` : 'manual'}</td>
                      <td>${d.note ?? html`<span class="muted">—</span>`}</td>
                    </tr>
                  `,
                )}
              </tbody>
            </table></div>
          `}
      <p class="muted sub">Everhour days arrive with the timesheet import; manual days are entered from Timesheets → Employees → Leave.</p>
    `;
  }

  private renderAnchors(s: LeaveSummary) {
    return html`
      <form @submit=${this.addAnchor}>
        <h4>Balance anchors</h4>
        <div class="full">
          ${s.anchors.length === 0 ? html`<p class="muted">None yet.</p>` : nothing}
          ${s.anchors.map(
            (a, i) => html`
              <div class="inline-field">
                <span>
                  ${a.as_of}: <strong>${a.remaining_days}</strong> days remaining${a.note ? ` · ${a.note}` : ''}
                  ${i === 0 ? html`<wa-tag size="small">current</wa-tag>` : nothing}
                </span>
                <wa-button size="small" appearance="plain" variant="danger" ?disabled=${this.saving} @click=${() => this.removeAnchor(a)}>Remove</wa-button>
              </div>
            `,
          )}
        </div>
        <wa-input name="as_of" label="As of" type="date" required></wa-input>
        <wa-input name="remaining_days" label="Remaining days" inputmode="decimal" required placeholder="12.5"></wa-input>
        <wa-input class="full" name="note" label="Note" placeholder="Carried over from the payroll sheet"></wa-input>
        <div class="full"><wa-button type="submit" size="small" appearance="outlined" ?loading=${this.saving}>Add anchor</wa-button></div>
      </form>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vacation-dialog': VacationDialog;
  }
}
